import { Chip } from "@dropbox/dig-components/chip";
import { useRecentPrompts } from "@mirage/mosaics/Results/useRecentPrompts";
import { useFirstIntersection } from "@mirage/mosaics/SearchPage/CategorySearchAccordion/useFirstIntersection";
import { service as computeRelevanceService } from "@mirage/service-compute-relevance";
import type { RelevanceRubric } from "@mirage/service-compute-relevance/service/types";
import type { SearchResult } from "@mirage/service-dbx-api";
import { tagged } from "@mirage/service-logging";
import type { RelevanceResult } from "@mirage/shared/modals/ComputeRelevanceModal";
import { ComputeRelevanceModal } from "@mirage/shared/modals/ComputeRelevanceModal";
import RelevanceRatingIcon from "@mirage/shared/relevance/icon";
import type { Rating } from "@mirage/shared/relevance/rating";
import RelevanceRating from "@mirage/shared/relevance/rating";
import type { Recommendation } from "@mirage/shared/search/recommendation";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import { onKeyDownCommitFn } from "@mirage/shared/util/on-key-down";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./Relevance.module.css";

const logger = tagged("Search Relevance");

export default function SearchRelevance({
  rubric,
  query,
  activeFilters: _activeFilters,
  searchResult,
  chip = true,
  noIcon = false,
  base64Image,
}: {
  rubric: RelevanceRubric;
  query: string;
  activeFilters?: SearchFilter[];
  searchResult: SearchResult | Recommendation;
  chip?: boolean;
  noIcon?: boolean;
  base64Image?: string;
}) {
  const [computeRelevanceForItem, setComputeRelevanceForItem] =
    useState<boolean>(false);
  const [collectedRelevanceResult, setCollectedRelevanceResult] =
    useState<string>("");
  const [finalRelevanceResult, setFinalRelevanceResult] =
    useState<RelevanceResult | null>(null);
  const [foundRelevanceRating, setFoundRelevanceRating] =
    useState<Rating | null>(null);
  const [relevanceLoading, setRelevanceLoading] = useState<boolean>(true);
  const [relevanceError, setRelevanceError] = useState<Error | null>(null);
  const [isStreamComplete, setIsStreamComplete] = useState<boolean>(false);

  const [prompt, setPrompt] = useState<string | null>(null);
  const [userMessageContent, setUserMessageContent] = useState<string | null>(
    null,
  );
  const { setSelectedPromptId } = useRecentPrompts(rubric);

  // Watch for changes in collectedRelevanceResult and update rating accordingly
  useEffect(() => {
    if (collectedRelevanceResult) {
      const rating = collectedRelevanceResult.match(/rating.*?([0-9X]).*/);
      if (rating) {
        setFoundRelevanceRating(rating[1] as Rating);
      }
    }
  }, [collectedRelevanceResult]);

  // Handle completion logic when stream is complete
  useEffect(() => {
    if (isStreamComplete && collectedRelevanceResult) {
      setRelevanceLoading(false);

      try {
        const parsedResult: RelevanceResult = JSON.parse(
          collectedRelevanceResult,
        );
        setFinalRelevanceResult(parsedResult);
        setPrompt(parsedResult.sentPrompt);
        setUserMessageContent(parsedResult.userMessageContent);
        if ("promptId" in parsedResult && parsedResult.promptId) {
          setSelectedPromptId(parsedResult.promptId);
        } else {
          setSelectedPromptId(null);
        }
      } catch (error) {
        logger.error("Cannot parse relevance", error);
        if (error instanceof Error) {
          setRelevanceError(error);
        } else {
          throw error;
        }
      }

      logger.debug("Compute relevance complete");
    }
  }, [isStreamComplete, collectedRelevanceResult, setSelectedPromptId]);

  const handleRelevanceResult = useMemo(
    () => ({
      next: (res: string) => {
        setCollectedRelevanceResult((prev) => prev + res);
      },
      error: (error: Error) => {
        setRelevanceError(() => error);
        logger.error("Cannot fetch relevance", error);
      },
      complete: () => {
        setIsStreamComplete(true);
      },
    }),
    [],
  );

  const loadSearchRelevanceForQuery = useCallback(() => {
    setRelevanceLoading(true);
    setIsStreamComplete(false);
    setCollectedRelevanceResult("");
    setFoundRelevanceRating(null);
    setFinalRelevanceResult(null);
    setRelevanceError(null);

    const subscription = computeRelevanceService
      .computeRelevance({
        item: searchResult,
        base64Image,
        query,
        rubric,
      })
      .subscribe(handleRelevanceResult);

    return () => subscription.unsubscribe();
  }, [searchResult, base64Image, query, rubric, handleRelevanceResult]);

  const loadSearchRelevanceForPrompt = useCallback(() => {
    if (prompt) {
      setRelevanceLoading(true);
      setIsStreamComplete(false);
      setCollectedRelevanceResult("");
      setFoundRelevanceRating(null);
      setFinalRelevanceResult(null);
      setRelevanceError(null);

      const subscription = computeRelevanceService
        .computeRelevance({
          item: searchResult,
          prompt,
          query,
          rubric,
        })
        .subscribe(handleRelevanceResult);

      return () => subscription.unsubscribe();
    }
  }, [prompt, searchResult, query, rubric, handleRelevanceResult]);

  const searchRelevanceRef = useFirstIntersection(loadSearchRelevanceForQuery);

  const handleClick = () => {
    setComputeRelevanceForItem(true);
  };

  const isDisabled = relevanceLoading && !foundRelevanceRating;
  const rating = foundRelevanceRating || finalRelevanceResult?.rating;
  const showRating = foundRelevanceRating || finalRelevanceResult;

  // Common content elements
  const iconElement = <RelevanceRatingIcon rating={rating} />;
  const contentElement = showRating ? (
    <RelevanceRating ratingOnly rating={rating} />
  ) : (
    <span
      className={classNames(
        styles.relevanceLoadingIndicator,
        relevanceError && styles.relevanceError,
      )}
    />
  );

  return (
    <span
      role="button"
      tabIndex={0}
      ref={searchRelevanceRef}
      onClick={(e) => {
        e.stopPropagation();
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
      }}
    >
      {
        // We have clicked the chip
        !!computeRelevanceForItem && (
          <ComputeRelevanceModal
            open={!!computeRelevanceForItem}
            onClose={() => setComputeRelevanceForItem(false)}
            query={query}
            item={searchResult}
            error={relevanceError}
            result={
              finalRelevanceResult || {
                rating: foundRelevanceRating,
                // Match everything after "explanation": "
                // Until an ending (unescaped) double quote
                explanation: collectedRelevanceResult.match(
                  /[\s\S.]*"explanation": "([^"\\]*(?:\\.[^"\\]*)*)["]?/,
                )?.[1],
              }
            }
            prompt={prompt}
            resetExplanation={() => {
              setCollectedRelevanceResult("");
              setFoundRelevanceRating(null);
              setFinalRelevanceResult(null);
              setIsStreamComplete(false);
            }}
            rubric={rubric}
            setPrompt={setPrompt}
            userMessageContent={userMessageContent}
            submitPrompt={() => {
              // Reset state
              setCollectedRelevanceResult("");
              setFoundRelevanceRating(null);
              setFinalRelevanceResult(null);
              setIsStreamComplete(false);

              // Load the relevance using the latest prompt
              loadSearchRelevanceForPrompt();
            }}
            loading={relevanceLoading}
          />
        )
      }
      <DigTooltip title={i18n.t("search_relevance_tip_title")}>
        {chip ? (
          <Chip
            variant="transparent"
            onClick={handleClick}
            isSelected={false}
            selectedStyle="stroke"
            size="small"
            type="button"
            disabled={isDisabled}
            data-testid="SearchRelevanceChip"
          >
            {!noIcon && <Chip.IconAccessory>{iconElement}</Chip.IconAccessory>}
            <Chip.Content className={classNames(styles.text)}>
              {contentElement}
            </Chip.Content>
          </Chip>
        ) : (
          <span
            className={styles.noChip}
            onClick={handleClick}
            data-testid="SearchRelevance"
            role="button"
            tabIndex={0}
            onKeyDown={onKeyDownCommitFn(handleClick)}
          >
            {!noIcon && <span>{iconElement}</span>}
            <span className={classNames(styles.text)}>{contentElement}</span>
          </span>
        )}
      </DigTooltip>
    </span>
  );
}
